export const _ = {};

// lang

export function isObject(val) {
  return val !== null && typeof val === "object";
}

export function isFunction(val) {
  return typeof val === "function";
}

export function isPrimitive(val) {
  return !(isObject(val) || isFunction(val));
}

export const isArray = Array.isArray;

export function isString(val) {
  return typeof val === "string";
}

export const equals = (val1, val2) => val1 === val2;

export const not = val => !val;

// function

export const noop = () => {
};

export const identity = val => val;

export const constant = val => () => val;

export function conjunction(...fns) {
  return (...args) => (
    every(fns, callWith(...args))
  );
}

export function disjunction(...fns) {
  return (...args) => (
    some(fns, callWith(...args))
  );
}

export const alwaysTrue = /*@__PURE__*/constant(true);

export const alwaysFalse = /*@__PURE__*/constant(false);

export function negate(fn) {
  return compose(not, fn);
}

export const equal = val1 => val2 => val1 === val2;

export function equalAny(vals) {
  if (vals.length === 1) {
    return equal(vals[0]);
  } else {
    return val => contains(vals, val);
  }
}

export function tryCatch(fn, catchFn = constant(undefined)) {
  return (...args) => {
    try {
      return fn(...args);
    } catch (e) {
      return catchFn(...args);
    }
  };
}

export const property = prop => obj => obj[prop];

export const propertySafe = prop => obj => obj ? obj[prop] : undefined;

function propertyDeepImpl(obj, path, i) {
  if (i === path.length) {
    return obj;
  }

  if (obj) {
    return propertyDeepImpl(obj[path[i]], path, i + 1);
  }

  return undefined;
}

export function propertyDeep(path) {
  if (isString(path)) {
    path = path.split(".");
  }

  return obj => (
    propertyDeepImpl(obj, path, 0)
  );
}

export const propertyOf = obj => prop => obj[prop];

export function compose(fn1, ...fns) {
  if (fns.length) {
    const rest = compose(...fns);
    return (...args) => (
      fn1(rest(...args))
    );
  } else {
    return fn1;
  }
}

export function overArgs(fn, transforms) {
  return (...args) => (
    fn(...transforms.map((fn, i) => fn(args[i])))
  );
}

export function computeArgs(fn, transforms) {
  return (...args) => (
    fn(...transforms.map(callWith(...args)))
  );
}

export function rest(fn) {
  return args => (
    fn(...args)
  );
}

export function unrest(fn) {
  return (...args) => (
    fn(args)
  );
}

export const call = /*@__PURE__*/(
  fn => (
    fn()
  )
);

export function callWith(...args) {
  return fn => (
    fn(...args)
  );
}

export function callAll(...fns) {
  return (...args) => {
    fns.forEach(callWith(...args));
  };
}

export const nthArg = /*@__PURE__*/compose(unrest, property);

export function rearg(order, fn) {
  return computeArgs(fn, order.map(nthArg));
}

export function prefill(fn, ...preArgs) {
  return (...args) => (
    fn(...preArgs.map(arg => arg === _ ? args.shift() : arg).concat(args))
  );
}

function transformMatchCase([pred, val]) {
  if (!isFunction(pred)) {
    if (isObject(pred)) {
      pred = prefill(matchesDeep, _, pred);
    } else {
      pred = equal(pred);
    }
  }

  return [pred, val];
}

export function match(...cases) {
  cases = cases.map(transformMatchCase);

  return (...args) => (
    find(cases, ([fn]) => fn(...args))[1]
  );
}

export function matchInvokeDefaulted(dflt, ...cases) {
  const matchFn = match(...cases, [alwaysTrue, dflt]);

  return (...args) => (
    matchFn(...args)(...args)
  );
}

export function matchInvoke(...cases) {
  return matchInvokeDefaulted(noop, ...cases);
}

export function memoizeHashed(fn, hashFn) {
  const cache = {};

  return (
    (...args) => {
      const hash = hashFn(...args);

      if (hasOwn(cache, hash)) {
        return cache[hash];
      } else {
        return cache[hash] = fn(...args);
      }
    }
  );
}

export const memoizeSimple = /*@__PURE__*/prefill(memoizeHashed, _, identity);

const equalAll = (arr1, arr2) => {
  const len = arr1.length;

  if (arr2.length !== len) return false;

  for (let i = 0; i < len; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  return true;
};

export const memoizeLast = fn => {
  let lastArgs = [];
  let lastVal;
  let ran = false;
  return (...args) => {
    if (ran && equalAll(args, lastArgs)) return lastVal;
    ran = true;
    lastArgs = args;
    return lastVal = fn(...args);
  };
};

export const memoizeLastHashed = (fn, hashFn) => {
  let lastHash;
  let lastVal;
  let ran = false;
  return (...args) => {
    const hash = hashFn(...args);
    if (ran && hash === lastHash) return lastVal;
    ran = true;
    lastHash = hash;
    return lastVal = fn(...args);
  };
};

export function invoke(prop, ...args) {
  return obj => (
    obj[prop](...args)
  );
}

export function bound(obj, prop) {
  return (...args) => (
    obj[prop](...args)
  );
}

export function boundArgs(obj, prop, ...args) {
  return () => (
    obj[prop](...args)
  );
}

// array

export const contains = (arr, item) => arr.indexOf(item) >= 0;

export function findIndex(arr, pred) {
  for (let i = 0; i < arr.length; i++) {
    if (pred(arr[i])) {
      return i;
    }
  }

  return -1;
}

export const findLastIndex = (arr, pred) => {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i])) {
      return i;
    }
  }

  return -1;
}

export function find(arr, pred) {
  const idx = findIndex(arr, pred);
  if (idx >= 0) {
    return arr[idx];
  } else {
    return undefined;
  }
}

export function some(arr, pred) {
  return findIndex(arr, pred) >= 0;
}

export function every(arr, pred) {
  return !some(arr, negate(pred));
}

export function countIf(arr, pred) {
  return arr.reduce((acc, item) => acc + !!pred(item), 0);
}

export function partition(arr, pred) {
  return arr.reduce((partitions, item) => (partitions[+!!pred(item)].push(item), partitions), [[], []]);
}

function multimapInsert(map, key, val) {
  const arr = hasOwn(map, key) ? map[key] : (map[key] = []);
  arr.push(val);
}

export function partitionKeyed(arr, keyFn) {
  return arr.reduce((partitions, item) => (multimapInsert(partitions, keyFn(item), item), partitions), {});
}

export const first = arr => arr[0];

export const last = arr => arr[arr.length - 1];

export const zip = (...arrs) => {
  const zipped = [];
  let i = Math.min(...arrs.map(property("length")));
  while (--i >= 0) zipped[i] = arrs.map(property(i));
  return zipped;
};

export const filterInPlace = (arr, pred) => {
  const len = arr.length;
  let j = 0;

  for (let i = 0; i < len; i++) {
    const val = arr[i];
    if (pred(val)) arr[j++] = val;
  }

  arr.length = j;
  return arr;
}

function flattenN(arr, depth) {
  // concat was slow in FF
  /*if (depth <= 0 || !isArray(arr)) {
    return arr;
  }*/

  // return arr.reduce((acc, val) => [...acc, ...flattenN(val, depth - 1)], []);

  const out = [];
  function visit(val, depth) {
    if (depth < 0 || !isArray(val)) {
      out.push(val);
    } else {
      for (let i = 0; i < val.length; i++) {
        visit(val[i], depth - 1);
      }
    }
  }

  visit(arr, depth);

  return out;
}

export const flatten = /*@__PURE__*/prefill(flattenN, _, 1);

export const flattenDeep = /*@__PURE__*/prefill(flattenN, _, Infinity);

export const range = (range, fn = identity) => {
  if (typeof range === "number") range = [0, range];
  const [start, end] = range;
  const arr = [];
  for (let i = start; i < end; i++) arr.push(fn(i));
  return arr;
};

const uniqFilterFn = (val, i, arr) => arr.indexOf(val) === i;

export function uniq(arr) {
  return arr.filter(uniqFilterFn);
}

export function insertUniq(arr, val) {
  if (contains(arr, val)) {
    return false;
  } else {
    arr.push(val);
    return true;
  }
}

export function arrayRemove(arr, val) {
  const idx = arr.indexOf(val);
  if (idx >= 0) {
    arr.splice(idx, 1);
    return true;
  } else {
    return false;
  }
}

const protoMap = Array.prototype.map;

export function map(arr, fn) {
  return protoMap.call(arr, fn);
}

export function allEqual(arr) {
  return every(arr, equal(arr[0]));
}

// object

export const keys = Object.keys;

export function values(obj) {
  return keys(obj).map(propertyOf(obj));
}

const protoHasOwnProperty = Object.prototype.hasOwnProperty;
export const hasOwn = /*@__PURE__*/(Object.hasOwn || ((obj, prop) => protoHasOwnProperty.call(obj, prop)));

export function condPickMap(obj, filterFn, keys, mapFn) {
  return keys.reduce(
    (picked, key) => (filterFn(obj[key], key, obj) && (picked[key] = mapFn(obj[key], key, obj)), picked),
    {}
  );
}

export function pick(obj, keys) {
  return condPickMap(obj, (val, key, obj) => hasOwn(obj, key), keys, identity);
}

export function mapObject(obj, fn) {
  return condPickMap(obj, alwaysTrue, keys(obj), fn);
}

function mapObjectDeepImpl(obj, fn, key, parent) {
  if (isObject(obj)) {
    return mapObject(obj, (val, key, obj) => mapObjectDeepImpl(val, fn, key, obj));
  } else {
    return fn(obj, key, parent);
  }
}

export function mapObjectDeep(obj, fn) {
  return mapObjectDeepImpl(obj, fn, undefined, undefined);
}

export function filterObject(obj, fn) {
  return condPickMap(obj, fn, keys(obj), identity);
}

export function mapObjectKeys(obj, fn) {
  return keys(obj).reduce((acc, key) => (acc[fn(key)] = obj[key], acc), {});
}

const assignPonyfill = (target, ...sources) => {
  sources.forEach(source => {
    if (!source) return;
    forEach(source, (val, key) => {
      target[key] = val;
    });
  });

  return target;
};

export const assign = Object.assign || assignPonyfill;

export function assignDeep(target, ...sources) {
  sources.forEach(source => {
    if (!every([target, source], isObject)) {
      target = source;
    } else {
      forEach(source, (val, key) => {
        target[key] = assignDeep(target[key], source[key]);
      })
    }
  });

  return target;
}

export function toPairs(obj) {
  return keys(obj).map(key => [key, obj[key]]);
}

export function fromPairs(pairs) {
  return pairs.reduce((obj, [key, value]) => (obj[key] = value, obj), {});
}

export function flip(obj) {
  return fromPairs(toPairs(obj).map(p => p.reverse()));
}

function cloneObject(obj) {
  return assign({}, obj);
}

function cloneArray(arr) {
  return arr.slice();
}

export const clone = /*@__PURE__*/matchInvokeDefaulted(identity, [isArray, cloneArray], [isObject, cloneObject]);

function cloneDeepObject(obj) {
  return mapObject(obj, cloneDeep);
}

function cloneDeepArray(arr) {
  return arr.map(cloneDeep);
}

export const cloneDeep = /*@__PURE__*/matchInvokeDefaulted(identity, [isArray, cloneDeepArray], [isObject, cloneDeepObject]);

export function matches(obj, pattern) {
  return isObject(obj) && every(keys(pattern), computeArgs(equals, [propertyOf(obj), propertyOf(pattern)]));
}

export function matchesDeep(obj, pattern) {
  if (isObject(pattern)) {
    return isObject(obj) && every(keys(pattern), computeArgs(matchesDeep, [propertyOf(obj), propertyOf(pattern)]));
  } else {
    return obj === pattern;
  }
}

export const forEach = (val, fn) => {
  if (isArray(val)) val.forEach(fn);
  else keys(val).forEach(key => { fn(val[key], key, val); });
};

// time

export const debounce = (fn, delay) => {
  let token = null;
  let savedArgs = [];
  let savedThis = null;

  const call = () => {
    token = null;
    fn.apply(savedThis, savedArgs);
  };

  return function (...args) {
    clearTimeout(token);
    token = setTimeout(call, delay);
    savedArgs = args;
    savedThis = this;
  };
};

export const throttle = (fn, delay) => {
  let token = null;
  let savedArgs = [];
  let savedThis = null;

  const call = () => {
    token = null;
    fn.apply(savedThis, savedArgs);
  };

  return function (...args) {
    savedArgs = args;
    savedThis = this;
    if (token) return;
    token = setTimeout(call, delay);
  };
};

export const throttleLeading = (fn, delay) => {
  let enabled = true;

  const call = () => {
    enabled = true;
  };

  return function (...args) {
    if (!enabled) return;
    fn.apply(this, args);
    enabled = false;
    setTimeout(call, delay);
  };
};

export const throttleFrame = fn => {
  let scheduled = false;
  let savedArgs = [];
  let savedThis;

  function call() {
    scheduled = false;
    fn.apply(savedThis, savedArgs);
  }

  return function (...args) {
    savedArgs = args;
    savedThis = this;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(call);
  }
};

export const eachFrame = fn => {
  const tick = time => {
    token = requestAnimationFrame(tick);
    fn(time);
  }

  let token = requestAnimationFrame(tick);

  return () => cancelAnimationFrame(token);
};

export const nextTick = fn => {
  setTimeout(fn, 0);
};

export const nextFrame = fn => {
  requestAnimationFrame(fn);
};

// update

class MutWrap {
  constructor(val) {
    this.val = val;
  }

  setProp(key, value) {
    if (!isObject(this.val)) this.val = {};
    this.val[key] = value;
    return this;
  }

  concat(items) {
    if (isArray(this.val)) this.val.push(...items);
    else this.val = items;
    return this;
  }

  remove(items) {
    if (isArray(this.val)) filterInPlace(this.val, negate(equalAny(items)));
    else this.val = [];
    return this;
  }

  invoke(fn) {
    this.val = fn(this.val);
    return this;
  }
}

class ImmutWrap {
  constructor(val) {
    this.val = val;
  }

  setProp(key, value) {
    if (!isObject(this.val)) return new MutWrap({[key]: value});
    if (this.val[key] === value) return this;
    const obj = clone(this.val);
    obj[key] = value;
    return new MutWrap(obj);
  }

  concat(items) {
    if (items.length === 0) return this;
    return new MutWrap(isArray(this.val) ? this.val.concat(items) : items);
  }

  remove(items) {
    if (isArray(this.val)) {
      const filtered = this.val.filter(negate(equalAny(items)));
      if (filtered.length === this.val.length) return this;
      else return new MutWrap(filtered);
    } else {
      return new MutWrap([]);
    }
  }

  invoke(fn) {
    const val = fn(this.val);
    if (val === this.val) return this;
    return new MutWrap(val);
  }
}

const CMD_SET = "$set";
const CMD_CONCAT = "$concat";
const CMD_REMOVE = "$remove";
const CMD_INVOKE = "$invoke";
const CMD_FIND = "$find";

export const immutableUpdate = (val, updates) => {
  if (!isObject(updates)) return updates;
  if (updates.hasOwnProperty(CMD_SET)) return updates[CMD_SET];

  let wrap = new ImmutWrap(val);

  keys(updates).forEach(key => {
    switch (key) {
      case CMD_CONCAT:
        wrap = wrap.concat(updates[CMD_CONCAT]);
        break;
      case CMD_REMOVE:
        wrap = wrap.remove(updates[CMD_REMOVE]);
        break;
      case CMD_INVOKE:
        wrap = wrap.invoke(updates[CMD_INVOKE]);
        break;
      case CMD_FIND: {
        if (!isObject(wrap.val)) break;
        const [pred, nestedUpdates] = updates[CMD_FIND];
        forEach(wrap.val, (val, key) => {
          if (pred(val)) {
            wrap = wrap.setProp(key, immutableUpdate(val, nestedUpdates));
          }
        });
      }
        break;
      default: {
        const subVal = isObject(wrap.val) && hasOwn(wrap.val, key) ? wrap.val[key] : isArray(updates[key]) ? [] : {};
        wrap = wrap.setProp(key, immutableUpdate(subVal, updates[key]));
      }
    }
  });

  return wrap.val;
};
